import React, { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './FAQ.css'

gsap.registerPlugin(ScrollTrigger)

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false)
  const bodyRef = useRef(null)

  useEffect(() => {
    if (!bodyRef.current) return
    gsap.to(bodyRef.current, {
      height: open ? 'auto' : 0, 
      duration: 0.4, 
      ease: 'power2.inOut',
      onStart: () => { if (open && bodyRef.current) bodyRef.current.style.overflow = 'visible' },
      onComplete: () => { if (!open && bodyRef.current) bodyRef.current.style.overflow = 'hidden' }
    })
  }, [open])

  return (
    <div className='faq-item-row border-b border-soft transition-colors duration-200'>
      <button onClick={() => setOpen(!open)}
        className='faq-question-btn w-full text-left py-5 flex items-start justify-between gap-6 transition-colors duration-200'
        style={{ color: open ? 'var(--accent)' : 'var(--fg)' }}>
        <span className='faq-q-text text-base font-semibold'>{question}</span>
        <span className='faq-plus-icon flex-shrink-0 mt-0.5 text-lg font-light transition-transform duration-300 w-6 h-6 rounded-full border flex items-center justify-center'
          style={{ transform: open ? 'rotate(45deg)' : 'none', borderColor: open ? 'var(--accent)' : 'var(--border)', color: 'var(--accent)' }}>+</span>
      </button>
      <div ref={bodyRef} style={{ height: 0, overflow: 'hidden' }}>
        <p className='faq-answer-text pb-5 text-sm leading-relaxed max-w-2xl text-muted text-pretty'>{answer}</p>
      </div>
    </div>
  )
}

export default function FAQ() {
  const sectionRef = useRef(null)

  const faqs = [
    { 
      q: "What does 'refurbished' mean?", 
      a: "Refurbished devices are professionally restored pre-owned products that meet our strict quality standards. Each device is thoroughly inspected, tested, cleaned, and repaired to work like new." 
    },
    { 
      q: "What warranty do you provide?", 
      a: "We provide a comprehensive 6-month warranty covering hardware and software issues, plus a 7-day return policy for complete peace of mind." 
    },
    { 
      q: "How much can I save?", 
      a: "Save 30-70% compared to new devices. A ₹80,000 laptop might cost just ₹35,000-50,000 refurbished, without compromising quality." 
    },
    { 
      q: "How do you ensure quality?", 
      a: "Our 4-step process includes hardware inspection, professional cleaning, component repair, and final testing. Each device passes 50+ diagnostic tests." 
    },
    { 
      q: "What's your return policy?", 
      a: "Hassle-free 7-day returns with free shipping. Full refunds processed within 3-5 business days if you're not completely satisfied." 
    },
    { 
      q: "Is buying online safe?", 
      a: "Absolutely! We use secure payments, provide detailed descriptions with real photos, offer warranties, and have thousands of satisfied customers." 
    }
  ]

  const splitWords = (text, startDelay = 0) => {
    const words = text.trim().split(/\s+/);
    return words.map((word, i) => (
      <span
        key={`${word}-${i}`}
        className="word-animate"
        style={{ animationDelay: `${startDelay + i * 40}ms` }}
      >
        {word}
        {i < words.length - 1 ? ' ' : ''}
      </span>
    ));
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Simplified entrance: no 'y' movement, just opacity and trigger visible class
      gsap.to(sectionRef.current, { 
        opacity: 1, 
        duration: 0.8, 
        scrollTrigger: { 
          trigger: sectionRef.current, 
          start: 'top 70%',
          onEnter: () => sectionRef.current.classList.add('visible')
        } 
      })
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  return (
    <section id='faq' ref={sectionRef} className='faq-section opacity-0 bg-tint section-fade'>
      <div className='faq-container max-w-7xl mx-auto grid md:grid-cols-2 gap-14 md:gap-20'>
        <div className='faq-sidebar self-start'>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-6 h-[1px] bg-website-blue" style={{ backgroundColor: 'var(--accent)' }} />
            <span className="font-mono-label text-[9px] font-bold text-website-blue uppercase tracking-[0.5em]" style={{ color: 'var(--accent)' }}>
              FAQ
            </span>
          </div>
          <h2 className="faq-headline text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
            {splitWords('Everything you', 100)}{' '}
            <span className="blue-accent inline-block" style={{ color: 'var(--accent)' }}>
              {splitWords('need to know.', 240)}
            </span>
          </h2>
        </div>
        <div className='faq-list'>
          {faqs.map((item, i) => <FAQItem key={i} question={item.q} answer={item.a} />)}
        </div>
      </div>
    </section>
  )
}